import { OnboardingDialogStep } from "@components/utils/OnboardingDialog/constants";
import { Paths } from "@constants/paths";
import { useDailyRewardModalTrigger } from "@hooks/useDailyRewardModalTrigger";
import { _MemberSettingOnboardDialogLayout } from "@types";
import { lazy, Suspense, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const LazyBankAccountStep = lazy(() => import("../BankAccountStep"));
const LazyBankLegalNameStep = lazy(() => import("../BankLegalNameStep"));
const LazyLegalNameStep = lazy(() => import("../LegalNameStep"));

interface PostLoginInfoStepProps {
  step: OnboardingDialogStep;
  stepNavigate: (step: OnboardingDialogStep) => void;
  handleClose: () => void;
  layout: _MemberSettingOnboardDialogLayout;
  callback?: () => void;
  handleBack?: () => void;
  redirectHomeOnSuccess?: boolean;
}

/**
 * Post login info prompt (persistent prompt)
 * Display appropriate prompt based on step
 */
const PostLoginInfoStep = ({
  step,
  stepNavigate,
  handleClose,
  layout,
  callback,
  handleBack,
  redirectHomeOnSuccess,
}: PostLoginInfoStepProps) => {
  const routerNavigate = useNavigate();
  const { openModal } = useDailyRewardModalTrigger();

  const handleOnUpdateSuccess = () => {
    if (callback) {
      callback();
    }
    if (redirectHomeOnSuccess) {
      routerNavigate(Paths.PRIVATE.HOME);
    }
    // Trigger Daily Login modal if eligible after collection is completed
    // This ensures the modal shows after collection drawer is completed (not just dismissed)
    openModal();
  };

  // Handle dismissal - trigger Daily Login modal if eligible
  const handleDismiss = useCallback(() => {
    handleClose();
    // Trigger Daily Login modal if eligible (will be checked by the hook)
    openModal();
  }, [handleClose, openModal]);

  return (
    <>
      {/*Funding Mode: BankOnly*/}
      {[OnboardingDialogStep.UpdateBankAccount, OnboardingDialogStep.AddBankAccount].includes(step) && (
        <Suspense>
          <LazyBankAccountStep
            navigate={stepNavigate}
            onUpdateSuccess={handleOnUpdateSuccess}
            handleClose={handleDismiss}
            layout={layout}
            step={step}
            handleBack={handleBack}
          />
        </Suspense>
      )}

      {/*Funding Mode: EWalletOnly*/}
      {[OnboardingDialogStep.UpdateLegalName, OnboardingDialogStep.AddLegalName].includes(step) && (
        <Suspense>
          <LazyLegalNameStep
            navigate={stepNavigate}
            handleClose={handleDismiss}
            onUpdateSuccess={handleOnUpdateSuccess}
            layout={layout}
            step={step}
            handleBack={handleBack}
          />
        </Suspense>
      )}

      {/*Funding Mode: BankAndEWallet*/}
      {[OnboardingDialogStep.UpdateBankLegalName, OnboardingDialogStep.AddBankLegalName].includes(step) && (
        <Suspense>
          {/*Requirement changed: BankLegalNameStep -> LegalNameStep*/}
          {/* <LazyBankLegalNameStep
              navigate={stepNavigate}
              onUpdateSuccess={handleOnUpdateSuccess}
              handleClose={handleClose}
              layout={layout}
              step={step}
            /> */}
          <LazyLegalNameStep
            navigate={stepNavigate}
            handleClose={handleDismiss}
            onUpdateSuccess={handleOnUpdateSuccess}
            layout={layout}
            step={step}
            handleBack={handleBack}
          />
        </Suspense>
      )}
    </>
  );
};

export default PostLoginInfoStep;
